import {
  capabilitySlugs,
  type CapabilitySlug,
  type IndustrySlug,
  type SeoContent,
} from "./contentSchema";

export { capabilitySlugs };
export type { CapabilitySlug };

export type CapabilityRecord = SeoContent & {
  image?: string;
  slug: CapabilitySlug;
  title: string;
  summary: string;
  headline: string;
  heroCopy: string[];
  offerings: string[];
  principle: { headline: string; copy: string[] };
  typicalQuestions: string[];
  relatedCapabilities: CapabilitySlug[];
  relatedIndustries: IndustrySlug[];
};

export const capabilitiesIndexPage = {
  image: "/images/capabilities/capabilities-index-hero.webp",
  seoTitle: "Engineering Capabilities | Applied AI, Search & Systems",
  seoDescription:
    "General Applications engineers applied AI, search platforms, workflow automation, and software architecture for complex operational environments.",
  headline:
    "The hard part is not adding technology. **It is making the whole system work.**",
  heroCopy: [
    "AI depends on trustworthy retrieval. Search depends on coherent data. Automation depends on explicit workflow state, exception handling, and system authority. Software architecture determines whether all of it remains understandable after the first release.",
    "General Applications works across those boundaries because real systems do not arrive divided into convenient technical specialties. They arrive as fragmented information, brittle processes, aging applications, conflicting sources, operational constraints, and teams that still have to deliver while the system is being changed.",
  ],
  introTitle: "Four capabilities, **one engineering system**.",
  introCopy: [
    "Our capabilities form one engineering system rather than four isolated service lines.",
    "Intelligent systems add reasoning and assistance. Data, search, and knowledge systems establish the information those systems can trust. Automation and operational tooling move work through real processes. Software architecture and engineering provide the product, application, integration, and delivery foundations that hold everything together.",
    "Work may begin with architecture and technical strategy, a focused implementation, modernization of an existing system, or embedded engineering leadership. The objective is the same: leave behind a system that is more coherent, more operable, and safer to extend.",
  ],
} as const;

export const capabilities: CapabilityRecord[] = [
  {
    slug: "intelligent-systems",
    image: "/images/capabilities/intelligent-systems-hero.webp",
    title: "Intelligent Systems",
    summary:
      "Applied AI, retrieval, evaluation, and decision-support systems engineered with explicit controls, review paths, and human accountability.",
    seoTitle: "Intelligent Systems & Applied AI | General Applications",
    seoDescription:
      "General Applications engineers applied AI systems: retrieval-backed workflows, evaluation, and deterministic controls for real operating environments.",
    headline:
      "Applied AI engineered as part of the system, not added beside it.",
    heroCopy: [
      "A model is only one component of an intelligent system. Its usefulness depends on the information it can retrieve, the context it receives, the tools it can use, the actions it is permitted to take, and the workflow that determines what happens next.",
      "Weak surrounding systems produce confident answers from stale sources, hide uncertainty, bypass approvals, and turn isolated demonstrations into operational liabilities.",
      "General Applications engineers intelligent systems with grounded retrieval, structured context, evaluation, deterministic controls, and explicit human accountability built into the architecture from the start.",
    ],
    offerings: [
      "AI readiness assessments, use-case selection, and staged implementation plans",
      "Retrieval-backed assistants grounded in approved documents, data, and system authority",
      "Decision-support systems with explicit review, approval, override, and escalation paths",
      "Agentic and tool-using workflows with bounded permissions and observable actions",
      "Human-in-the-loop authoring, research, classification, and revision systems",
      "Evaluation harnesses, domain test sets, validation checks, and release quality gates",
      "Structured context assembly, schema-constrained outputs, and model and tool orchestration",
      "Model and provider selection, routing, fallback, performance, and cost controls",
      "Production monitoring for retrieval quality, model behavior, tool execution, and workflow outcomes",
      "Focused prototypes designed to answer real technical and operational questions before broader investment",
    ],
    principle: {
      headline: "Probabilistic systems still need deterministic controls.",
      copy: [
        "A model can generate options, summaries, classifications, plans, and drafts. It should not silently decide what is authoritative, complete, permitted, safe, or ready to act on.",
        "The dependable system is built around the model: authoritative retrieval, structured context, schema validation, permissions, evaluation, fallback behavior, review paths, instrumentation, and clear ownership of every consequential decision.",
      ],
    },
    typicalQuestions: [
      "Where can AI improve this workflow without obscuring authority or accountability?",
      "What information, tools, and actions should the system be permitted to use?",
      "When should the system use retrieval, tools, deterministic rules, model customization, or no model at all?",
      "What must be validated before an output can be trusted or acted on?",
      "Where should human review, approval, or override remain mandatory?",
      "What evidence would demonstrate that the system is improving the real workflow rather than merely producing plausible output?",
      "How do we keep a useful prototype from becoming an operational liability?",
    ],
    relatedCapabilities: [
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
    relatedIndustries: [
      "energy",
      "agriculture",
      "enterprise",
      "healthcare",
      "military",
      "media-entertainment",
    ],
  },
  {
    slug: "data-search-knowledge",
    image: "/images/capabilities/data-search-knowledge-hero.webp",
    title: "Data, Search & Knowledge",
    summary:
      "Data architecture, search platforms, and knowledge systems that reconcile fragmented information and make reliable answers easier to find, explain, and use.",
    seoTitle: "Data, Search & Knowledge Systems | General Applications",
    seoDescription:
      "Hybrid search, canonical data models, knowledge ingestion, and relevance controls that make complex information findable and explainable.",
    headline:
      "Search, data, and knowledge systems people can actually trust and use.",
    heroCopy: [
      "Organizations often have more data than agreement, more sources than ownership, and more search interfaces than useful results.",
      "Information ends up across relational systems, documents, vendor platforms, APIs, spreadsheets, product catalogs, operational tooling, search indexes, and the memory of whoever gets called when something breaks.",
      "General Applications engineers the models, ingestion pipelines, retrieval strategies, relevance controls, and interfaces that turn fragmented information into dependable systems people and AI can actually use.",
    ],
    offerings: [
      "Enterprise search and internal discovery systems",
      "Product search, catalog discovery, and relevance architecture",
      "Hybrid retrieval systems combining relational, lexical, and vector approaches",
      "Knowledge models, taxonomies, and structured content foundations",
      "Retrieval foundations for AI assistants, technical content, and knowledge workflows",
      "Data normalization, reconciliation, and canonical entity modeling",
      "Indexing, ingestion, enrichment, and schema-governance pipelines",
      "Search architecture reviews, provider selection, and migration planning",
      "Search quality evaluation, index validation, and drift detection",
    ],
    principle: {
      headline: "Search quality starts in the data, not the AI.",
      copy: [
        "Search systems fail when underlying entities are inconsistent, source authority is unclear, the taxonomy is weak, ingestion drifts, schema changes go unnoticed, and relevance becomes a stack of guesses nobody can explain.",
        "An LLM can improve how people ask questions and how results are presented, but it cannot compensate for weak source data, an incoherent canonical model, a poor indexing strategy, the wrong retrieval method, or relevance controls that cannot be measured and defended.",
      ],
    },
    typicalQuestions: [
      "Why can people still not find the right answer even when the information exists?",
      "Which source should be authoritative, and where do conflicting records need to be reconciled?",
      "What should be modeled as canonical data versus indexed for retrieval?",
      "When should retrieval be lexical, semantic, vector-based, or hybrid?",
      "What needs to be searchable, versioned, enriched, or excluded?",
      "How do we detect schema and source drift before it degrades indexing or relevance?",
      "How should search quality be measured and improved on purpose?",
      "How do we create retrieval that AI can use without multiplying errors?",
    ],
    relatedCapabilities: [
      "intelligent-systems",
      "automation-operations",
      "software-architecture-engineering",
    ],
    relatedIndustries: [
      "energy",
      "agriculture",
      "enterprise",
      "healthcare",
      "military",
      "media-entertainment",
    ],
  },
  {
    slug: "automation-operations",
    image: "/images/capabilities/automation-operations-hero.webp",
    title: "Automation & Operations",
    summary:
      "Workflow automation, operational tooling, and system integration designed around real handoffs, exceptions, decisions, and recovery.",
    seoTitle: "Automation & Operations | General Applications",
    seoDescription:
      "Workflow design, system integration, exception handling, and operational tooling that make status, responsibility, and next actions easy to see.",
    headline:
      "Operational systems that still work when the workflow gets messy.",
    heroCopy: [
      "Many organizations have automation. Far fewer have automation that people understand, recover from, and improve when work stops matching the diagram.",
      "Operational work depends on handoffs, exceptions, approvals, measurements, asset states, compliance obligations, and people who still need to understand what the system is doing.",
      "General Applications engineers operational systems that reduce repetitive work, make system state easier to see, surface exceptions early, and preserve human judgment where accountability still matters.",
    ],
    offerings: [
      "Workflow assessments and process redesign for exception-heavy operations",
      "Workflow automation for operational and compliance-heavy environments",
      "Operational dashboards, support tooling, and control interfaces",
      "Operational integration across control, historian, device, asset, maintenance, enterprise, and workflow systems",
      "Exception handling, escalation, and fallback flows",
      "Approval, review, and governed-change systems",
      "Decision-support systems for operators, analysts, and specialists",
      "Human-in-the-loop operations with explicit review, intervention, and recovery paths",
      "Instrumentation, status modeling, and operational visibility",
    ],
    principle: {
      headline: "Automate repetition. Preserve judgment.",
      copy: [
        "Good automation removes unnecessary effort while making the state of the system easier to see and the next decision easier to make.",
        "It does not hide exceptions, erase review, or turn a broken process into a faster broken process.",
      ],
    },
    typicalQuestions: [
      "Where does work actually stall, and what is that delay costing us?",
      "Which handoffs, approvals, or exceptions create the most rework?",
      "What should happen automatically, and what should remain reviewable?",
      "Which systems need to exchange state, and where is that exchange failing?",
      "How should exceptions be surfaced, routed, and recovered from?",
      "What operational state do teams need to see in order to trust and improve the process?",
      "How do we improve the process without replacing useful flexibility with brittle automation?",
    ],
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "software-architecture-engineering",
    ],
    relatedIndustries: [
      "energy",
      "agriculture",
      "enterprise",
      "healthcare",
      "military",
      "media-entertainment",
    ],
  },
  {
    slug: "software-architecture-engineering",
    image: "/images/capabilities/software-architecture-engineering-hero.webp",
    title: "Software Architecture & Engineering",
    summary:
      "Software architecture and full-stack engineering that make complex products and operational systems easier to build, operate, change, and sustain.",
    seoTitle: "Software Architecture & Engineering | General Applications",
    seoDescription:
      "Software architecture, modernization, integration, and engineering leadership for applications and systems that need to evolve safely.",
    headline:
      "Software architecture and delivery foundations built to survive contact with reality.",
    heroCopy: [
      "Architecture is not a diagram saved after implementation. It is the set of decisions that determines whether a product can evolve, teams can deliver safely, and the system remains understandable after the original plan meets real constraints.",
      "That includes product behavior, user interfaces, service boundaries, data contracts, delivery practices, permission models, testability, observability, and the operational consequence of each choice.",
      "General Applications designs and implements the foundation: product boundaries, user workflows, search and data contracts, integration patterns, architectural decisions, and delivery practices that make systems easier to build, change, operate, and support.",
    ],
    offerings: [
      "Architecture reviews and implementation planning",
      "Product architecture, implementation strategy, and staged modernization plans",
      "Frontend, backend, and full-stack system design",
      "Application and platform modernization with targeted technical-debt reduction",
      "API and integration design",
      "Domain, permission, and data-contract modeling",
      "Delivery foundations and CI/CD patterns",
      "Testing strategy and release-safety improvements",
      "Environment, deployment, and operational readiness planning",
      "Reliability, supportability, and observability practices",
      "Embedded technical leadership that improves implementation, coordination, and delivery quality",
    ],
    principle: {
      headline: "Think ahead. Build only what the system has earned.",
      copy: [
        "The goal is not to build an empire of abstractions before the work deserves it.",
        "The goal is to create enough structure that the system can grow, teams can coordinate, and future changes do not recreate the same mistakes in a more expensive stack.",
      ],
    },
    typicalQuestions: [
      "What should be built first, and what foundation will keep later work coherent?",
      "Where should product boundaries, service boundaries, permissions, and ownership actually live?",
      "Which integrations are durable enough to formalize, and which should stay temporary?",
      "How should the product, workflows, services, data contracts, and search layer fit together?",
      "What technical debt is genuinely slowing delivery, reliability, or change?",
      "What needs standardization, and what should remain deliberately simple?",
      "How do we make the system safer to build, test, deploy, operate, and extend?",
    ],
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "automation-operations",
    ],
    relatedIndustries: [
      "energy",
      "agriculture",
      "enterprise",
      "healthcare",
      "military",
      "media-entertainment",
    ],
  },
];

export function getCapabilityBySlug(
  slug: string,
): CapabilityRecord | undefined {
  return capabilities.find((capability) => capability.slug === slug);
}
